// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { cn } from '@/lib/utils';


export const ServerAvatar = ({
  imageUrl,
  name,
  seed,
  alt = 'Server Avatar',
  imageSize = 48,
  fontSize = 28,
  className,
}: {
  imageUrl?: string;
  name: string;
  seed?: string;
  alt?: string;
  imageSize?: number;
  fontSize?: number;
  className?: string;
}) => {
  if (seed && !imageUrl) {
    const bgColor = getRandomColor(seed);
    const textColor = getOppositeColor(bgColor, true);

    return (
      <div
        style={{
          fontSize: fontSize,
          background: bgColor,
          color: textColor,
        }}
        className={cn('flex text-center items-center justify-center font-bold w-full', className)}
      >
        {name.slice(0, 2).toUpperCase()}
      </div>
    );
  } else {
    if (imageUrl)
      return (
        <img
          src={`${imageUrl}`}
          alt={alt}
          loading="lazy"
          className={cn('w-full', className)}
          style={{ width: imageSize, height: imageSize }}
        />
      );
  }
};

const padZero = (str: string, len = 2) => {
  const zeros = new Array(len).join('0');
  return (zeros + str).slice(-len);
};

/**
 * Deterministic color generator based on a seed string.
 * Always returns a valid 6-digit hex color, e.g. #a3b2c1.
 */
export const getRandomColor = (seed: string) => {
  let hash = 0;

  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & 0xffffffff; // Force a 32-bit integer
  }

  const colorInt = Math.abs(hash) % 0xffffff; // ensure 0 – FFFFFF
  const hex = padZero(colorInt.toString(16), 6);

  return `#${hex}`;
};

/**
This function takes a color hex value and returns the opposite color. It also takes a boolean value,
which when set to true will return either white or black, depending on which color is closest to the given color.
Parameters
hex
 - a hexadecimal value representing a color
bw
 - a boolean value, which when set to true will return either white or black, depending on which color is closest to the given color
Return Value
A hexadecimal value representing the opposite color.
Example Usage
getOppositeColor('#FF0000', false); // will return '#00FFFF'
getOppositeColor('#FF0000', true); // will return '#FFFFFF'
 */

/**
 * This function takes a color hex value and returns the opposite color. It also takes a boolean value,
    which when set to true will return either white or black, depending on which color is closest to the given color.
 * @param hex a hexadecimal value representing a color
 * @param bw a boolean value, which when set to true will return either white or black, depending on which color is closest to the given color
Return Value
 * @returns A hexadecimal value representing the opposite color.
 */

export const getOppositeColor = (hex: string, bw: boolean) => {
  if (hex.indexOf('#') === 0) {
    hex = hex.slice(1);
  }

  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    throw new Error('Invalid HEX color.');
  }

  const r = parseInt(hex.slice(0, 2), 16),
    g = parseInt(hex.slice(2, 4), 16),
    b = parseInt(hex.slice(4, 6), 16);

  if (bw) {
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#FFFFFF';
  }

  // invert color components
  const r16 = (255 - r).toString(16);
  const g16 = (255 - g).toString(16);
  const b16 = (255 - b).toString(16);

  return '#' + padZero(r16) + padZero(g16) + padZero(b16);
};
